// Fires a GA4 event (and a Meta Pixel `Lead`, when `fbq` is passed) when an
// ActiveCampaign embedded form reaches its in-place success state.
//
// AC forms (`._form_<id>`) are injected inline after their embed script loads
// and submit over their own AJAX — on success they swap in a `._form-thank-you`
// panel rather than navigating to a thank-you page. GA4's automatic
// `form_submit` therefore never maps to the site's custom key events
// (`contact_us`, `find_a_retailer`), which is why those key events show
// "No stream data detected". We watch the form container for that success panel
// becoming visible and fire the exact, parameter-less event name GA4 counts.
//
// Returns a cleanup function — call it on component destroy to disconnect the
// observer (SPA route changes detach the container).
export function trackAcFormSuccess({ container, event, gtag, fbq }) {
  if (typeof window === "undefined" || !container || !gtag) {
    return () => {}
  }

  let fired = false

  const check = () => {
    if (fired) return
    const thankYou = container.querySelector("._form-thank-you")
    // getClientRects().length is 0 while the panel is absent or display:none,
    // and > 0 once AC actually renders it — covers both the "injected on
    // success" and "pre-rendered then shown" variants of the AC embed.
    if (thankYou && thankYou.getClientRects().length > 0) {
      fired = true
      gtag.event(event)
      if (fbq) fbq("Lead", { content_name: event })
      observer.disconnect()
    }
  }

  const observer = new MutationObserver(check)
  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class"]
  })

  // Handle the panel already being visible when we attach.
  check()

  return () => observer.disconnect()
}

// Fires a GA4 `file_download` event for a downloadable link.
//
// GA4's Enhanced Measurement fires `file_download` on its own, but only when it
// can spot a known extension in the link URL. Hygraph serves every asset from an
// extensionless handle URL
// (https://<region>.graphassets.com/<environment>/<handle>), so no dealer PDF
// matches: they are counted, at best, as generic outbound clicks on a URL nobody
// can map back to a document. Firing the event ourselves is the only way to
// attribute a click to a named file.
//
// ⚠️ Only call this for links Enhanced Measurement CANNOT see. A link whose URL
// ends in a real extension is already counted by GA4 — the warranty PDFs at
// /pdf/*.pdf and the S3 product spec sheets both are, which is why neither calls
// this — and firing our event as well counts every click twice. Instrumenting
// those would mean first switching off GA4's Admin → Data Streams → Enhanced
// Measurement → "File downloads" toggle, which is global and would silently stop
// the product-page downloads from being counted at all.
//
// The event and parameter names are GA4's own on purpose — the values land in
// the built-in "File name" / "File extension" / "Link text" / "Link URL"
// dimensions, so no custom definitions are needed and these downloads aggregate
// with the ones Enhanced Measurement finds by itself.
//
// `page_location` is sent explicitly (it is what separates /dealer/downloads
// from /dealer/information in the report). gtag snapshots the location when
// `config` runs and does not re-read it on a pushState navigation unless GA4's
// "page changes based on browser history events" is on, so an event fired after
// a client-side route change can otherwise be attributed to whichever page the
// dealer happened to load first.
//
// `url` is the link target and `title` the label shown next to it; `fileName`
// and `mimeType` come from the Hygraph asset behind the link.
export function trackFileDownload({ gtag, url, title, fileName, mimeType }) {
  if (typeof window === "undefined" || !gtag || !url) return

  const name = fileName || title || ""

  gtag.event("file_download", {
    file_name: name,
    file_extension: fileExtension(name, mimeType),
    link_text: title || name,
    link_url: url,
    page_location: window.location.href
  })
}

// "Barrel-Sauna-Spec-Sheet.pdf" -> "pdf". Hygraph assets uploaded without an
// extension in the name still carry a mimeType ("application/pdf"), so fall
// back to its subtype rather than reporting an empty extension.
function fileExtension(fileName, mimeType) {
  const match = /\.([a-z0-9]+)$/i.exec(fileName)

  if (match) return match[1].toLowerCase()

  return (mimeType || "").split("/").pop().toLowerCase()
}
